import { deriveDungeonEnemyPreview } from './entrance-preview.js';
import { deriveDungeonOmenMarkers } from './omen-markers.js';
import { generateDungeonSafe } from './generate.js';
import { normaliseSeed, SeededRandom } from './random.js';
import { DungeonEntranceError, } from './entrance-types.js';
import { validateDungeonEnemyIconRules, validateDungeonEntranceSite, validateDungeonEntranceState, validateDungeonShrineProfiles, } from './entrance-validate.js';
function horizontalDistance(left, right) {
    return Math.hypot(left.x - right.x, left.z - right.z);
}
function validateObservers(observers) {
    const problems = [];
    const ids = new Set();
    for (const [index, observer] of observers.entries()) {
        const path = `observers[${index}]`;
        if (typeof observer.playerId !== 'string' || observer.playerId.length === 0) {
            problems.push({
                code: 'invalid-observer-id',
                path: `${path}.playerId`,
                message: 'must be non-empty',
            });
        }
        else if (ids.has(observer.playerId)) {
            problems.push({
                code: 'duplicate-observer-id',
                path: `${path}.playerId`,
                message: observer.playerId,
            });
        }
        ids.add(observer.playerId);
        if (!observer.position ||
            ![observer.position.x, observer.position.y, observer.position.z].every((value) => typeof value === 'number' && Number.isFinite(value))) {
            problems.push({
                code: 'invalid-observer-position',
                path: `${path}.position`,
                message: 'must contain finite x/y/z',
            });
        }
        if (!Number.isInteger(observer.partySize) || observer.partySize < 1 || observer.partySize > 8) {
            problems.push({
                code: 'invalid-observer-party-size',
                path: `${path}.partySize`,
                message: 'must be an integer in [1, 8]',
            });
        }
    }
    return problems;
}
function faulted(site, state, cycleSeed, playerId, problems) {
    return {
        state: {
            phase: 'faulted',
            siteId: site.id,
            generationCycle: state.generationCycle,
            cycleSeed,
            activatedByPlayerId: playerId,
            problems,
        },
        events: [
            {
                kind: 'generation-failed',
                siteId: site.id,
                generationCycle: state.generationCycle,
                playerId,
                problemCodes: problems.map((problem) => problem.code),
            },
        ],
    };
}
/** Pure one-site transition. It generates only on dormant -> observed boundary crossing. */
export function updateDungeonEntranceLifecycle(input) {
    const profile = input.shrineProfiles.find((candidate) => candidate.difficultyId === input.site.difficultyId);
    const inputProblems = [
        ...validateDungeonShrineProfiles(input.shrineProfiles, new Set(input.catalog.difficulties.map((entry) => entry.id))),
        ...validateDungeonEnemyIconRules(input.iconRules),
        ...validateDungeonEntranceSite(input.site, profile),
        ...validateDungeonEntranceState(input.site, input.state),
        ...validateObservers(input.observers),
    ];
    if (inputProblems.length > 0)
        throw new DungeonEntranceError(inputProblems);
    const resetObservers = input.observers.filter((observer) => horizontalDistance(observer.position, input.site.position) <= input.site.radii.resetRadius);
    if (input.state.phase !== 'dormant' && resetObservers.length === 0) {
        const nextCycle = input.state.generationCycle + 1;
        const event = {
            kind: 'reset',
            siteId: input.site.id,
            previousCycle: input.state.generationCycle,
            nextCycle,
            ...(input.state.phase === 'primed'
                ? { previousPlanFingerprint: input.state.plan.fingerprint }
                : {}),
        };
        return {
            state: { phase: 'dormant', siteId: input.site.id, generationCycle: nextCycle },
            events: [event],
        };
    }
    if (input.state.phase !== 'dormant')
        return { state: input.state, events: [] };
    const generationObservers = input.observers
        .map((observer) => ({
        observer,
        distance: horizontalDistance(observer.position, input.site.position),
    }))
        .filter((entry) => entry.distance <= input.site.radii.generationRadius)
        .sort((left, right) => left.distance - right.distance ||
        left.observer.playerId.localeCompare(right.observer.playerId));
    const activation = generationObservers[0];
    if (!activation)
        return { state: input.state, events: [] };
    const cycleSeed = `entrance-cycle::${normaliseSeed(input.worldSeed)}::${input.site.id}::${input.state.generationCycle}`;
    const themeCandidates = input.site.themeIds
        .map((themeId) => input.catalog.themes.find((theme) => theme.id === themeId))
        .filter((theme) => Boolean(theme) &&
        input.site.targetTier >= (theme?.tierRange.min ?? Infinity) &&
        input.site.targetTier <= (theme?.tierRange.max ?? -Infinity));
    if (themeCandidates.length === 0) {
        return faulted(input.site, input.state, cycleSeed, activation.observer.playerId, [
            {
                code: 'entrance-no-eligible-theme',
                path: 'site.themeIds',
                message: `no site theme supports target tier ${input.site.targetTier}`,
            },
        ]);
    }
    const random = new SeededRandom(cycleSeed);
    const theme = random.pick([...themeCandidates].sort((left, right) => left.id.localeCompare(right.id)));
    const result = generateDungeonSafe({
        seed: cycleSeed,
        difficultyId: input.site.difficultyId,
        themeId: theme.id,
        targetTier: input.site.targetTier,
        partySize: activation.observer.partySize,
        ...(input.history ? { history: input.history } : {}),
    }, input.catalog);
    if (!result.ok) {
        return faulted(input.site, input.state, cycleSeed, activation.observer.playerId, result.problems);
    }
    try {
        const preview = deriveDungeonEnemyPreview(result.plan, input.catalog, input.iconRules);
        const markers = deriveDungeonOmenMarkers(result.plan, input.catalog, input.iconRules);
        return {
            state: {
                phase: 'primed',
                siteId: input.site.id,
                generationCycle: input.state.generationCycle,
                cycleSeed,
                activatedByPlayerId: activation.observer.playerId,
                plan: result.plan,
                preview,
                markers,
            },
            events: [
                {
                    kind: 'primed',
                    siteId: input.site.id,
                    generationCycle: input.state.generationCycle,
                    playerId: activation.observer.playerId,
                    planFingerprint: result.plan.fingerprint,
                    previewIconIds: preview.slots.map((slot) => slot.iconId),
                },
            ],
        };
    }
    catch (error) {
        const problems = error instanceof DungeonEntranceError
            ? error.problems
            : [
                {
                    code: 'entrance-preview-failed',
                    path: 'preview',
                    message: error instanceof Error ? error.message : String(error),
                },
            ];
        return faulted(input.site, input.state, cycleSeed, activation.observer.playerId, problems);
    }
}
export function updateDungeonEntranceWorld(input) {
    const nextSites = {};
    const events = [];
    for (const site of [...input.sites].sort((left, right) => left.id.localeCompare(right.id))) {
        const previous = input.state.sites[site.id] ?? {
            phase: 'dormant',
            siteId: site.id,
            generationCycle: 0,
        };
        const result = updateDungeonEntranceLifecycle({
            worldSeed: input.worldSeed,
            site,
            state: previous,
            observers: input.observers,
            catalog: input.catalog,
            shrineProfiles: input.shrineProfiles,
            iconRules: input.iconRules,
            ...(input.historyBySite?.[site.id] ? { history: input.historyBySite[site.id] } : {}),
        });
        nextSites[site.id] = result.state;
        events.push(...result.events);
    }
    return { state: { sites: nextSites }, events };
}
//# sourceMappingURL=entrance-lifecycle.js.map